import React from "react";
import { useTranslation } from "react-i18next";
import useAnalyticsContext from "../../hooks/useAnalyticsContext.js";
import { trackSectionViewed, useSectionOnceObserver } from "../../lib/analytics";
import { WSE_SECTION } from "./sections.js";

/**
 * Questions about wedding seating.
 *
 * A plain definition list, always expanded — same reasoning as the four sibling
 * FAQs: every answer is short enough to read in place, and these are the page's
 * most self-contained factual passages, which is exactly what an assistant
 * quotes. Putting them behind an accordion would hide them from a reader and
 * gain nothing from a crawler.
 *
 * Every answer is checked against the implementation. The six that are easiest
 * to get wrong, and what the code actually says:
 *
 *  - `whoAppears` / `pending` — `buildReceptionPool`
 *    (api/services/seatingPool.mjs) admits a member only when the guest is
 *    `status: "active"` AND `approvalStatus: "approved"`, the member's
 *    `reviewStatus` is approved-or-absent, and they hold at least one
 *    "attending" response to an EventSection with `type: "reception"` and
 *    `isRSVPRequired`. A guest-added companion is written
 *    `reviewStatus: "pendingReview"` and is therefore absent until approved.
 *    Note this is deliberately NOT the same set as `attendingHeadcount`, which
 *    spans every section and applies no `reviewStatus` filter — which is why no
 *    answer here says the headcount IS the seating list.
 *  - `capacity` / `overCapacity` — `SeatingTableSchema.capacity` is
 *    `min: 1, max: 100`, and `assertTableHasRoom` throws
 *    `SEATING_TABLE_AT_CAPACITY` (409) before any assignment is written. The
 *    one route to an over-capacity table is `PUT /tables/:tableId` lowering the
 *    capacity of an already-seated table, which then surfaces as the advisory
 *    `OVER_CAPACITY` issue. Both halves are stated.
 *  - `together` / `apart` — `SeatingPreference` carries `keepPartyTogether`,
 *    `mustSitWithGuestIds`, `shouldAvoidGuestIds` and `priorityLevel`, and
 *    `computeSeatingInsights` emits SPLIT_PARTY / MUST_SIT_UNMET /
 *    AVOID_CONFLICT from them. Severity is advisory only: nothing in the
 *    codebase blocks an assignment on a preference, and the assign drawer
 *    offers "place anyway". "Guidance rather than a rule" is literal.
 *  - `cancels` — `classifyStaleReason` returns `not_attending | pending |
 *    removed`; the seat is flagged as STALE_ASSIGNMENT and still counts toward
 *    `occupiedSeatCount`. Nothing deletes it. The answer must never be softened
 *    into the seat being freed.
 *  - `dragDrop` — the disambiguation answer, and the only place on this page
 *    where "drag-and-drop" and "canvas" may appear at all. There is no DnD code
 *    anywhere under app/src/pages/couple/seating/, and no floor plan: `shape`
 *    is a label with an icon, never geometry. The forbidden-claims test pins
 *    that exception to this key.
 *  - `eros` — `plan_seating` writes only a `SeatingPlan` draft and never a
 *    `SeatingAssignment`; `apply_seating_plan` is a separate call and snapshots
 *    the previous board for one-level revert. Éros is blocked outright in the
 *    sandbox, which the answer states rather than leaving to the demo note.
 */
const FAQ_KEYS = [
  "what",
  "tables",
  "capacity",
  "whoAppears",
  "pending",
  "together",
  "apart",
  "cancels",
  "overCapacity",
  "move",
  "dragDrop",
  "export",
  "guestLookup",
  "eros",
  "plan",
];

const SeatingFaq = () => {
  const { t } = useTranslation();
  const analyticsContext = useAnalyticsContext();

  const sectionRef = useSectionOnceObserver(() =>
    trackSectionViewed(WSE_SECTION.FAQ, analyticsContext),
  );

  return (
    <section ref={sectionRef} className="section topless wps-section">
      <div className="container">
        <div className="wps-section-header">
          <h2 className="heading-50">{t("weddingSeating.faq.heading")}</h2>
        </div>

        <dl className="wps-faq">
          {FAQ_KEYS.map((key) => (
            <div key={key} className="wps-faq-item">
              <dt className="made-22 wps-faq-q">
                {t(`weddingSeating.faq.items.${key}.q`)}
              </dt>
              <dd className="wps-faq-a body-15">
                {t(`weddingSeating.faq.items.${key}.a`)}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
};

export default SeatingFaq;
